import React, { useState, useEffect, useRef } from 'react';
import { Search, X, Upload, Loader2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { useDebouncedValue } from '@/lib/utils';
import { useContentImport } from '@/hooks/shared/useContentImport';
import { useContentSelection } from '@/hooks/shared/useContentSelection';
import { ContentItemCard } from './ContentItemCard';
import { ImportEmptyFallback } from './ImportEmptyFallback';

interface UniversalContentImporterProps {
  contentType: 'images' | 'research' | 'pages';
  projectId: string;
  onBulkImport: (selectedIds: string[]) => Promise<void>;
  layout?: 'list' | 'grid';
  showBulkActions?: boolean;
  showClearButton?: boolean;
  showSearch?: boolean;
  className?: string;
}

export const UniversalContentImporter: React.FC<UniversalContentImporterProps> = ({
  contentType,
  projectId,
  onBulkImport,
  layout = 'list',
  showBulkActions = true,
  showClearButton = true,
  showSearch = true,
  className
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const debouncedSearch = useDebouncedValue(searchQuery, 300);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Import functionality
  const {
    data: allItems,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    totalCount
  } = useContentImport({
    contentType,
    projectId,
    searchQuery: debouncedSearch
  });

  // Selection functionality
  const {
    selectedItems,
    toggleItemSelection,
    clearSelection,
    isSelected,
    selectedCount
  } = useContentSelection();

  // Intersection observer for infinite scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      },
      { threshold: 0.1 }
    );

    if (loadMoreRef.current) {
      observer.observe(loadMoreRef.current);
    }

    return () => observer.disconnect();
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Clear search
  const handleClearSearch = () => {
    setSearchQuery('');
  };

  // Handle bulk import
  const handleBulkImport = async () => {
    if (selectedCount === 0) return;
    
    setIsImporting(true);
    try {
      await onBulkImport(Array.from(selectedItems));
      clearSelection(); // Clear selections after successful import
    } catch (error) {
      console.error('Error importing items:', error);
    } finally {
      setIsImporting(false);
    }
  };

  return (
    <div className={cn("flex flex-col h-full", className)}>
      {/* Search Bar - Moved to top */}
      {showSearch && (
        <div className="px-4 py-3 border-b border-gray-200/50 dark:border-gray-700/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 z-10 pointer-events-none" size={16} />
            <Input
              placeholder={`Search ${contentType === 'pages' ? 'documents' : contentType} to add...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 pr-9 relative z-0"
            />
            {searchQuery && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 z-10"
                title="Clear search"
                aria-label="Clear search"
              >
                <X size={16} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Action Buttons Row - Always visible but disabled when no items selected */}
      {showBulkActions && (
        <div className="px-4 py-3 border-b border-gray-200/50 dark:border-gray-700/50">
          <div className="flex items-center gap-2">
            <Button
              onClick={handleBulkImport}
              disabled={selectedCount === 0 || isImporting}
              className={cn(
                showClearButton ? "flex-1 text-white" : "w-full text-white",
                selectedCount === 0 ? "cursor-not-allowed opacity-60" : "",
                contentType === 'pages'
                  ? selectedCount === 0 ? "bg-purple-400 hover:bg-purple-400" : "bg-purple-600 hover:bg-purple-700"
                  : contentType === 'research'
                  ? selectedCount === 0 ? "bg-green-400 hover:bg-green-400" : "bg-green-600 hover:bg-green-700"
                  : selectedCount === 0 ? "bg-blue-400 hover:bg-blue-400" : "bg-blue-600 hover:bg-blue-700"
              )}
              size="sm"
            >
              {isImporting ? (
                <>
                  <Loader2 size={14} className="mr-2 animate-spin" />
                  Adding to Project...
                </>
              ) : (
                <>
                  <Upload size={14} className="mr-2" />
                  Add to Project {selectedCount > 0 ? `(${selectedCount})` : ''}
                </>
              )}
            </Button>
            
            {showClearButton && (
              <Button
                onClick={clearSelection}
                disabled={selectedCount === 0}
                variant="outline"
                size="sm"
                className="hidden sm:inline-flex"
              >
                Clear Selection
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Items List/Grid */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && allItems.length === 0 ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 size={20} className="animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-500">Loading {contentType}...</span>
          </div>
        ) : allItems.length === 0 ? (
          <ImportEmptyFallback 
            contentType={contentType}
            searchQuery={searchQuery}
          />
        ) : (
          <div className={cn(
            "p-2",
            layout === 'grid' && contentType === 'images' 
              ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3"
              : ""
          )}>
            <AnimatePresence>
              {allItems.map((item: any, index: number) => (
                <ContentItemCard
                  key={item.id}
                  item={item}
                  contentType={contentType}
                  isSelected={isSelected(item.id)}
                  onToggleSelection={toggleItemSelection}
                  index={index}
                  layout={layout}
                />
              ))}
            </AnimatePresence>
            
            {/* Load More Trigger */}
            {hasNextPage && (
              <div ref={loadMoreRef} className="flex items-center justify-center py-4 col-span-full">
                {isFetchingNextPage ? (
                  <>
                    <Loader2 size={16} className="animate-spin text-gray-400 mr-2" />
                    <span className="text-sm text-gray-500">Loading more...</span>
                  </>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => fetchNextPage()}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <ChevronDown size={16} className="mr-2" />
                    Load More
                  </Button>
                )}
              </div>
            )}
            
            {/* End of Results */}
            {!hasNextPage && allItems.length > 0 && (
              <div className="flex items-center justify-center py-4 col-span-full">
                <span className="text-xs text-gray-400">
                  {allItems.length} of {totalCount} {contentType}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}; 